import { useEffect, type FormEventHandler, type ReactNode, type TouchEvent } from 'react';
import { dialogDismissButtonClass } from '../../styles/uiStandards';
import { lockDialogScroll } from '../../lib/websiteScroll';
import { X } from 'lucide-react';

type AppDialogProps = {
  open: boolean;
  title: ReactNode;
  children: ReactNode;
  footer?: ReactNode;
  /** Wrap body + footer in `<form>` when provided. */
  onSubmit?: FormEventHandler<HTMLFormElement>;
  maxWidthClass?: string;
  /** Optional class for header chrome row (title + dismiss). */
  headerClassName?: string;
  /** Optional class for footer chrome row. */
  footerClassName?: string;
  /** Optional overlay wrapper class (e.g. mobile gap above bottom nav). */
  overlayClassName?: string;
  /** Optional dialog shell class (e.g. max-height on mobile). */
  shellClassName?: string;
  /** Hide the title chrome row (e.g. product modal hero on mobile). */
  hideHeader?: boolean;
  /** Extra classes on the scrollable body region. */
  bodyClassName?: string;
  /** Lock page scroll while open (recommended for full-screen mobile sheets). */
  lockBodyScroll?: boolean;
  /** Optional header dismiss control (e.g. product card on mobile). */
  onDismiss?: () => void;
  dismissLabel?: string;
  /** Covers the entire dialog shell (e.g. enlarged product photo on desktop). */
  insetOverlay?: ReactNode;
  /** Block right-click context menu on the dialog shell. */
  suppressContextMenu?: boolean;
  /** Document direction for chrome layout (close button, footer alignment). */
  dir?: 'ltr' | 'rtl';
};

/**
 * Standard Catalog of Works dialog: fixed header, scroll-area body, pinned footer.
 * Use for all modals (forms, confirmations, detail panels).
 */
export function AppDialog({
  open,
  title,
  children,
  footer,
  onSubmit,
  maxWidthClass = 'max-w-md',
  headerClassName,
  footerClassName,
  overlayClassName,
  shellClassName,
  hideHeader = false,
  bodyClassName,
  lockBodyScroll = true,
  onDismiss,
  dismissLabel = 'Close',
  insetOverlay,
  suppressContextMenu = false,
  dir = 'ltr',
}: AppDialogProps) {
  useEffect(() => {
    if (!open || !lockBodyScroll) {
      return undefined;
    }
    return lockDialogScroll();
  }, [lockBodyScroll, open]);

  useEffect(() => {
    if (!open || !onDismiss) {
      return undefined;
    }

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onDismiss();
      }
    };

    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [open, onDismiss]);

  const onOverlayTouchMove = (event: TouchEvent<HTMLDivElement>) => {
    if (event.target === event.currentTarget) {
      event.preventDefault();
    }
  };

  if (!open) return null;

  const shell = (
    <>
      {!hideHeader ? (
        <div
          className={`relative flex shrink-0 items-center border-b border-[color-mix(in_srgb,var(--border-color)_70%,#94a3b8)] bg-[var(--dialog-chrome)] px-6 py-4 pe-14 ${headerClassName ?? ''}`}
        >
          <h2 className="text-lg font-semibold">{title}</h2>
          {onDismiss ? (
            <button
              type="button"
              onClick={onDismiss}
              aria-label={dismissLabel}
              className={dialogDismissButtonClass}
            >
              <X size={20} strokeWidth={2} aria-hidden />
            </button>
          ) : null}
        </div>
      ) : null}

      <div
        className={`scroll-area min-h-0 flex-1 touch-pan-y overflow-y-auto overscroll-y-contain bg-[var(--bg-primary)] px-6 py-4 ${bodyClassName ?? ''}`}
      >
        {children}
      </div>

      {footer && (
        <div
          className={`flex w-full shrink-0 gap-2 border-t border-[color-mix(in_srgb,var(--border-color)_70%,#94a3b8)] bg-[var(--dialog-chrome)] px-6 py-4 ${footerClassName ?? 'justify-end'}`}
        >
          {footer}
        </div>
      )}
    </>
  );

  return (
    <div
      className={`fixed inset-0 z-50 flex items-center justify-center overscroll-none bg-black/40 p-4 ${overlayClassName ?? ''}`}
      onTouchMove={onOverlayTouchMove}
    >
      <div
        role="dialog"
        aria-modal="true"
        dir={dir}
        className={`relative flex max-h-[min(90vh,720px)] w-full touch-pan-y flex-col overflow-hidden rounded-[var(--radius-dialog)] border border-[var(--border-color)] bg-[var(--bg-primary)] shadow-xl ${maxWidthClass} ${shellClassName ?? ''}`}
        onTouchMove={(event) => event.stopPropagation()}
        onContextMenu={suppressContextMenu ? (event) => event.preventDefault() : undefined}
      >
        {insetOverlay}
        {onSubmit ? (
          <form onSubmit={onSubmit} className="flex min-h-0 flex-1 flex-col">
            {shell}
          </form>
        ) : (
          <div className="flex min-h-0 flex-1 flex-col">{shell}</div>
        )}
      </div>
    </div>
  );
}

/** Shared field control styles for dialog forms. */
export const dialogFieldClass =
  'rounded-[var(--radius-control)] border border-[var(--border-color)] bg-[var(--bg-secondary)] px-3 py-2';

/** Price amount: typed manually — no native number spinner arrows. */
export const dialogFieldPriceClass =
  `${dialogFieldClass} [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none`;

export const dialogFieldHoverClass =
  'cursor-pointer transition-colors hover:border-[var(--accent-primary)] hover:bg-[var(--bg-primary)] hover:text-[var(--accent-primary)]';

export const dialogFieldReadonlyClass =
  'cursor-not-allowed bg-[var(--bg-secondary)] text-[var(--text-secondary)]';

/** Uppercase section label inside AppDialog forms (FLPBox / tenant product form). */
export const dialogSectionTitleClass =
  'text-xs font-bold uppercase tracking-widest text-[var(--text-secondary)] opacity-70';
